import { Fragment } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { SeparatedList, Text, Tooltip } from '@components';
import { ICargoBreakdownListItem, SEPARATED_LIST_SEPARATOR, TOOLTIP_POSITION } from '@types';
import styles from './CargoBreakdownList.module.scss';

export interface CargoBreakdownListProps {
	items: ICargoBreakdownListItem[];
	textType?: string;
	hideArrow?: boolean;
	singleLine?: boolean;
}

/* One "type · quantity · weight" line per cargo (or cargo type group), with its dimensions
   underneath and any freight descriptions in an info tooltip at the end of the cargo's last line.
   hideArrow drops the arrow and indent for layouts that list the breakdown plainly (e.g. the
   shipment Overview timeline); singleLine drops the arrow and keeps each cargo on one line,
   dimensions last (e.g. the Allocation modal's Booking Details). */
export const CargoBreakdownList = (props: CargoBreakdownListProps) => {
	const { items, textType, hideArrow = false, singleLine = false } = props;

	const renderInfo = (descriptions: string[]) => {
		if (!descriptions.length) return null;
		return (
			<Tooltip
				position={TOOLTIP_POSITION.TOP}
				text={<div className={styles.tooltip}>
					{descriptions.map((description, di) => <div key={di}>{description}</div>)}
				</div>}
			>
				<InfoOutlinedIcon data-testid={'@Ofload-CargoBreakdownList-Info'} className={`${styles.icon} ${styles.infoIcon}`} style={{ fontSize: '1rem' }} />
			</Tooltip>
		);
	}

	return (
		<>
			{items.map((item, index) => {
				const descriptions = (item.descriptions ?? []).filter(Boolean);
				const dimensions = item.length && item.width && item.height
					? `${item.length}cm x ${item.width}cm x ${item.height}cm`
					: '';
				if (singleLine) {
					return (
						<div key={index} className={styles.row}>
							<SeparatedList items={[...item.summary, dimensions]} textType={textType} separator={SEPARATED_LIST_SEPARATOR.DOT} />
							{renderInfo(descriptions)}
						</div>
					);
				}
				return (
					<Fragment key={index}>
						<div className={styles.row}>
							{!hideArrow && <SubdirectoryArrowRightIcon data-testid={'@Ofload-CargoBreakdownList-Arrow'} className={styles.icon} style={{ fontSize: '1rem' }} />}
							<SeparatedList items={item.summary} textType={textType} separator={SEPARATED_LIST_SEPARATOR.DOT} />
							{!dimensions && renderInfo(descriptions)}
						</div>
						{!!dimensions && <div className={`${styles.row} ${hideArrow ? '' : styles.indented}`}>
							<Text type={textType}>{dimensions}</Text>
							{renderInfo(descriptions)}
						</div>}
					</Fragment>
				);
			})}
		</>
	);
}
